import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/i18n/translations";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(latest > prev && latest > 180 && !open);
  });

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/servizi", label: t.nav.servizi, id: "servizi" },
    { to: "/team", label: t.nav.team, id: "team" },
    { to: "/contatti", label: t.nav.contatti, id: "contatti" },
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <motion.header
        animate={{ y: hidden ? "-100%" : "0%" }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-x-0 top-0 z-50 border-b border-linec bg-sand/85 backdrop-blur-md"
        data-testid="navbar"
      >
        <div className="mx-auto flex h-[116px] max-w-7xl items-center justify-between px-5 lg:px-10 lg:h-[124px]">
          <Link to="/" className="group" data-testid="nav-logo-link" aria-label="SWM Consulting">
            <Logo small />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={`relative text-sm font-semibold transition-colors duration-200 hover:text-terra ${
                  isActive(l.to) ? "text-terra" : "text-night"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-[2px] bg-terra transition-all duration-300 ${
                    isActive(l.to) ? "w-full" : "w-0"
                  }`}
                />
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <div
              className="flex items-center gap-1 rounded-full border border-linec bg-white/60 p-1"
              data-testid="lang-switcher"
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-switch-${l.code}`}
                  className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors duration-200 ${
                    lang === l.code
                      ? "bg-night text-sand"
                      : "text-mutedx hover:text-night"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Link
              to="/contatti"
              data-testid="nav-cta"
              className="rounded-full bg-terra px-5 py-2.5 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:bg-terra-dark"
            >
              {t.nav.cta}
            </Link>
          </div>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-full border border-linec bg-white/70 text-night lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-night px-6 py-6"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo dark />
              <button
                className="flex h-11 w-11 items-center justify-center rounded-full border border-sand/25 text-sand"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block border-b border-sand/10 py-4 font-serif text-4xl text-sand"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto flex items-center justify-between">
              <div className="flex gap-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`rounded-full px-4 py-2 font-mono text-xs font-semibold ${
                      lang === l.code ? "bg-terra text-sand" : "border border-sand/25 text-sand/70"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <Link
                to="/contatti"
                onClick={() => setOpen(false)}
                className="rounded-full bg-gold px-5 py-3 text-sm font-bold text-night"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
